package com.essntl.features.request.ui.addeditrequest.components

import androidx.compose.animation.AnimatedVisibility
import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.interaction.MutableInteractionSource
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.saveable.rememberSaveable
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.TransformOrigin
import androidx.compose.ui.graphics.graphicsLayer
import androidx.compose.ui.input.pointer.PointerIcon
import androidx.compose.ui.input.pointer.pointerHoverIcon
import androidx.compose.ui.text.input.KeyboardType
import com.essntl.features.client.ui.ClientItem
import com.essntl.features.request.ui.addeditrequest.AddEditRequestEvent
import com.essntl.features.request.ui.addeditrequest.AddEditRequestNavigation
import com.essntl.features.request.ui.addeditrequest.AddEditRequestState
import com.essntl.uikit.components.button.secondary.EsLargeSecondaryButton
import com.essntl.uikit.components.icons.EsIcons
import com.essntl.uikit.components.icons.EsLargeIcon
import com.essntl.uikit.components.input.EsInput
import com.essntl.uikit.components.spinner.EsSpinner
import com.essntl.uikit.components.utils.EsConstants
import com.essntl.uikit.components.utils.spacing
import com.essntl.uikit.rememberFind
import com.essntl.uikit.theme.esColorScheme
import com.essntl.uikit.theme.esTypography

@Composable
fun RequestClientInformation(
    state: AddEditRequestState,
    onEvent: (AddEditRequestEvent) -> Unit,
    onNavigation: (AddEditRequestNavigation) -> Unit,
    enabled: Boolean,
) {
    val request = state.request.value
    val isLoading = state.addEditRequest.isLoading

    var isExpanded by rememberSaveable { mutableStateOf(true) }

    Column(
        modifier = Modifier
            .fillMaxWidth()
            .background(
                color = MaterialTheme.esColorScheme.bgSecondary,
                shape = RoundedCornerShape(EsConstants.RoundedCornerSize),
            )
            .padding(
                vertical = 3.spacing,
                horizontal = 2.5f.spacing,
            ),
    ) {
        Row(
            horizontalArrangement = Arrangement.SpaceBetween,
            modifier = Modifier
                .fillMaxWidth(),
        ) {
            Text(
                text = "Travellers",
                color = MaterialTheme.esColorScheme.textPrimary,
                style = MaterialTheme.esTypography.s2SemiBold16,
            )

            EsLargeIcon(
                res = EsIcons.ArrowDown,
                modifier = Modifier
                    .graphicsLayer {
                        transformOrigin = TransformOrigin.Center
                        rotationZ = if (isExpanded) 180f else 0f
                    }
                    .clip(CircleShape)
                    .pointerHoverIcon(PointerIcon.Hand)
                    .clickable(
                        interactionSource = remember { MutableInteractionSource() },
                        indication = null,
                    ) {
                        isExpanded = !isExpanded
                    },
            )
        }

        AnimatedVisibility(isExpanded) {
            Column(
                horizontalAlignment = Alignment.CenterHorizontally,
                modifier = Modifier
                    .fillMaxWidth(),
            ) {
                Spacer(modifier = Modifier.height(3.spacing))

                EsInput(
                    value = request.guestCount.let { if (it > 0) "$it" else "" },
                    placeholder = " Count",
                    label = "Count",
                    enabled = enabled && !isLoading,
                    onValueChange = {
                        val guestCount = it.toIntOrNull() ?: 0
                        onEvent(
                            AddEditRequestEvent.UpdateRequestFields(
                                request.copy(
                                    guestCount = guestCount,
                                ),
                            ),
                        )
                    },
                    keyboardOptions = KeyboardOptions(
                        keyboardType = KeyboardType.Number,
                    ),
                    showClearButton = false,
                    modifier = Modifier.fillMaxWidth(),
                )

                Spacer(modifier = Modifier.height(2.spacing))

                EsInput(
                    value = request.guestDetails ?: "",
                    placeholder = "Type in the description (e.g. 2 parents, 1 teenager)",
                    label = "Travellers description",
                    enabled = enabled && !isLoading,
                    onValueChange = {
                        onEvent(
                            AddEditRequestEvent.UpdateRequestFields(
                                request.copy(
                                    guestDetails = it,
                                ),
                            ),
                        )
                    },
                    keyboardOptions = KeyboardOptions(
                        keyboardType = KeyboardType.Number,
                    ),
                    modifier = Modifier.fillMaxWidth(),
                )
                Spacer(modifier = Modifier.height(3.spacing))

                if (state.getAllClient.isLoading)
                    EsSpinner()
                else
                    request.clients?.forEachIndexed { index, clientId ->
                        val client = rememberFind(
                            state.getAllClient.data.orEmpty(),
                            clientId,
                        ) { client -> client.id }

                        if (client != null) {
                            ClientItem(
                                client = client,
                                enabled = enabled && !isLoading,
                                onEditClient = {
                                    onNavigation(AddEditRequestNavigation.EditClient(client))
                                },
                                onDeleteClient = {
                                    onEvent(AddEditRequestEvent.RemoveClient(client.id))
                                },
                                modifier = Modifier
                                    .fillMaxWidth(),
                            )

                            if (index != request.clients?.lastIndex)
                                Spacer(modifier = Modifier.height(1.spacing))
                        }
                    }

                Spacer(modifier = Modifier.height(3.spacing))

                EsLargeSecondaryButton(
                    onClick = {
                        onNavigation(AddEditRequestNavigation.AddClient)
                    },
                    text = "Add person",
                    suffixIconRes = EsIcons.Add,
                    enabled = enabled && !isLoading,
                    modifier = Modifier
                        .fillMaxWidth(),
                )
            }
        }
    }
}